<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { contactsApi } from '../api/contactsApi';
import type { ContactAddressCandidate, ContactAddressTypeCode } from '../types/contact.types';
import {
    filterCandidatesByAddressType,
    filterCandidatesBySearch,
    getCandidateDisplayLine,
} from '../utils/contactAddressSelect';

const props = withDefaults(
    defineProps<{
        modelValue: boolean;
        contactCd: string;
        filterAddressType?: ContactAddressTypeCode | null;
    }>(),
    { filterAddressType: null }
);

const emit = defineEmits<{
    (e: 'update:modelValue', val: boolean): void;
    (e: 'select', candidate: ContactAddressCandidate): void;
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

const dialog = ref(props.modelValue);
watch(() => props.modelValue, (v) => { dialog.value = v; });
watch(dialog, (v) => emit('update:modelValue', v));

const loading = ref(false);
const error = ref<string | null>(null);
const search = ref('');
const allCandidates = ref<ContactAddressCandidate[]>([]);
const selectedId = ref<string | null>(null);

/** Address Type 필터 적용 후 목록 (검색 전) — 칩 건수는 API dedupe 결과와 이 목록 기준 */
const candidatesForTypeFilter = computed(() =>
    filterCandidatesByAddressType(allCandidates.value, props.filterAddressType ?? undefined)
);

const displayCounts = computed(() => {
    const list = candidatesForTypeFilter.value;
    return {
        SPC: list.filter((c) => c.sourceSystem === 'SPC').length,
        SYNC: list.filter((c) => c.sourceSystem === 'SYNC').length,
        LETTAGA: list.filter((c) => c.sourceSystem === 'LETTAGA').length,
        total: list.length,
    };
});

const filteredCandidates = computed(() =>
    filterCandidatesBySearch(candidatesForTypeFilter.value, search.value)
);

async function loadCandidates() {
    const cd = String(props.contactCd || '').trim();
    if (!cd) {
        error.value = t('contact.No address candidates') || 'No address candidates';
        allCandidates.value = [];
        return;
    }
    loading.value = true;
    error.value = null;
    selectedId.value = null;
    try {
        const res: any = await contactsApi.getContactAddressCandidates(cd);
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        allCandidates.value = Array.isArray(payload?.candidates) ? payload.candidates : [];
    } catch (e: any) {
        error.value = e?.response?.data?.message || e?.message || 'Failed to load addresses';
        allCandidates.value = [];
    } finally {
        loading.value = false;
    }
}

watch(dialog, (open) => {
    if (open) {
        search.value = '';
        void loadCandidates();
    } else {
        selectedId.value = null;
    }
});

function toggleSelection(candidateId: string) {
    selectedId.value = selectedId.value === candidateId ? null : candidateId;
}

function applySelection() {
    const id = selectedId.value;
    if (!id) return;
    const row = filteredCandidates.value.find((c) => c.candidateId === id);
    if (!row) return;
    emit('select', row);
    dialog.value = false;
}
</script>

<template>
  <v-dialog v-model="dialog" max-width="1080" width="92vw" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center flex-wrap ga-2 py-3">
        <v-icon color="primary">mdi-map-marker-multiple</v-icon>
        <span class="text-truncate flex-grow-1">
          {{ t('contact.Select Address') || 'Select Address' }}
        </span>
        <v-chip v-if="displayCounts.total > 0" size="small" variant="tonal" color="primary">
          {{ displayCounts.total }} {{ t('contact.total') || 'total' }}
        </v-chip>
        <v-chip v-if="displayCounts.SPC > 0" size="small" variant="outlined">SPC {{ displayCounts.SPC }}</v-chip>
        <v-chip v-if="displayCounts.SYNC > 0" size="small" variant="outlined">SYNC {{ displayCounts.SYNC }}</v-chip>
        <v-chip v-if="displayCounts.LETTAGA > 0" size="small" variant="outlined">LETTAGA {{ displayCounts.LETTAGA }}</v-chip>
        <v-btn icon variant="text" size="small" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <div class="px-4 py-3">
        <v-text-field
          v-model="search"
          :placeholder="t('contact.Search addresses') || 'Search by address or type...'"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
        />
      </div>

      <v-card-text class="pa-0" style="max-height: 60vh; overflow-y: auto;">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="error" class="pa-4 text-error d-flex align-center ga-2">
          <v-icon>mdi-alert</v-icon>
          {{ error }}
        </div>
        <div v-else-if="filteredCandidates.length === 0" class="pa-8 text-center text-medium-emphasis">
          <v-icon size="48" color="grey-lighten-1">mdi-map-marker-off-outline</v-icon>
          <p class="mt-3 mb-0">{{ t('contact.No address candidates') || 'No address candidates' }}</p>
        </div>
        <v-table v-else density="compact" class="contact-address-select-table">
          <thead>
            <tr>
              <th style="width: 48px" />
              <th style="width: 110px">{{ t('contact.Source System') || 'Source System' }}</th>
              <th style="width: 140px">{{ t('contact.Address Type') || 'Address Type' }}</th>
              <th>{{ t('contact.Address') || 'Address' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filteredCandidates"
              :key="row.candidateId"
              class="cursor-pointer"
              @click="toggleSelection(row.candidateId)"
            >
              <td @click.stop>
                <v-checkbox-btn
                  :model-value="selectedId === row.candidateId"
                  density="compact"
                  @update:model-value="toggleSelection(row.candidateId)"
                />
              </td>
              <td class="text-body-2">
                <v-chip
                  size="x-small"
                  variant="tonal"
                  :color="row.sourceSystem === 'SPC' ? 'warning' : row.sourceSystem === 'SYNC' ? 'info' : 'primary'"
                  label
                >
                  {{ row.sourceSystem }}
                </v-chip>
              </td>
              <td class="text-body-2">{{ row.addressTypeLabel }}</td>
              <td class="text-caption">{{ getCandidateDisplayLine(row) }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn variant="text" @click="dialog = false">{{ t('contact.Cancel') || 'Cancel' }}</v-btn>
        <v-btn color="primary" variant="flat" :disabled="!selectedId" @click="applySelection">
          {{ t('contact.Apply') || 'Apply' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.contact-address-select-table tbody tr:hover {
  background: rgba(var(--v-theme-primary), 0.04);
}
.cursor-pointer {
  cursor: pointer;
}
</style>
